// @flow

import * as React from 'react';
import { useQuery } from 'urql';
import { useVDOMPortal } from '@ta/lithium.portals';
import type { AuthenticateRequest } from '@ta/login.types';
import useLogin from '@ta/login.login';
import * as Import from '@ta/platform.import';
import TermsOfUse from './TermsOfUse.gql';

const TermsOfUseOverlay = Import.component.onUsage(() => import('@ta/social.terms-of-use-gate'));

const FLOWS_THAT_SKIP_USERNAME_SCREEN = ['SAVES_COMBINED'];

/**
 * Login gate for social actions.
 * Makes sure the user is logged in, and has accepted the new Terms of Service and confirmed their new username before taking the action.
 * All social actions need to be blocked using this component.
 * Disables the action in read-only mode
 *
 * @author ekarpov
 * @since Sep 2018
 */
const useSocialLoginGate = (authRequest: AuthenticateRequest) => {
  const onTermsOfUseSuccess = React.useRef(undefined);
  const login = useLogin();

  const resolve = React.useCallback(() => {
    if (onTermsOfUseSuccess.current) {
      const promiseCallback = onTermsOfUseSuccess.current;
      onTermsOfUseSuccess.current = null;
      promiseCallback();
    }
  }, []);

  const { open: openTermsOfUse, isOpen: isTermsOfUseOpen } = useVDOMPortal(({ close }) => (
    <TermsOfUseOverlay
      onComplete={() => {
        close();
        resolve();
      }}
      forceTermsOfUse
      onClose={() => {
        close();
        onTermsOfUseSuccess.current = null;
      }}
    />
  ));
  const [touState, queryTermsOfUse] = useQuery({ query: TermsOfUse, pause: true });

  React.useEffect(() => {
    if (!touState.fetching && onTermsOfUseSuccess.current && !isTermsOfUseOpen) {
      if (!touState.data?.getTermOfUseAcceptanceInfo?.accepted) {
        openTermsOfUse();
      } else {
        resolve();
      }
    }
  }, [isTermsOfUseOpen, openTermsOfUse, resolve, touState]);

  const socialLogin = React.useCallback(
    () =>
      login(authRequest).then(() => {
        // short circuit so we don't show username screen for certain flows
        if (authRequest.flow && FLOWS_THAT_SKIP_USERNAME_SCREEN.includes(authRequest.flow)) {
          return Promise.resolve();
        }

        return new Promise(promiseResolve => {
          onTermsOfUseSuccess.current = promiseResolve;
          queryTermsOfUse();
        });
      }),
    [authRequest, login, queryTermsOfUse],
  );
  return socialLogin;
};

export default useSocialLoginGate;
